import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTenant } from "../context/tenantContext";

interface Tenant {
  id: string;
  name: string;
  logo?: string;
}

export const TenantSelection = () => {
  const navigate = useNavigate();
  const { setSelectedTenant, tenants: listTenants } = useTenant();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenantState] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const getTenantInfo = (tenantId: string): Tenant => {
          const cleanTenantId = tenantId.split(";")[0];
          if (cleanTenantId.toLowerCase().includes("aus")) {
            return {
              id: cleanTenantId,
              name: cleanTenantId,
            };
          }
          return {
            id: cleanTenantId,
            name: cleanTenantId,
          };
        };

        const tenants =
          listTenants?.map((tenant) => getTenantInfo(tenant)) || [];

        setTenants(tenants);

        // If there's only one tenant, select it automatically
        if (tenants.length === 1) {
          setSelectedTenant(tenants[0].id);
          navigate("/");
        }

        setLoading(false);
      } catch (err) {
        setError("Error loading organizations. Please try again.");
        setLoading(false);
      }
    };

    fetchTenants();
  }, [navigate, setSelectedTenant, listTenants]);

  const handleTenantSelect = (tenantId: string) => {
    setSelectedTenantState(tenantId);
    setSelectedTenant(tenantId);
    navigate("/");
  };

  if (loading) {
    return null;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FBFBFF] flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-custom-shadow w-full max-w-md text-center">
          <p className="text-redLight mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-quaternary text-white py-2 px-4 rounded hover:bg-quaternary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // If there are no tenants, show message
  if (tenants.length === 0) {
    return (
      <div className="min-h-screen bg-[#FBFBFF] flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-custom-shadow w-full max-w-md text-center">
          <p className="text-quinary">No organizations available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBFBFF] flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-custom-shadow w-full max-w-md">
        <h1 className="text-2xl font-bold text-center mb-8 text-quinary">
          Select your Organization
        </h1>

        <div className="space-y-4">
          {tenants.map((tenant) => (
            <button
              key={tenant.id}
              onClick={() => handleTenantSelect(tenant.id)}
              className={`w-full p-4 rounded-lg border-2 transition-all duration-200 flex items-center justify-between
                ${
                  selectedTenant === tenant.id
                    ? "border-quaternary bg-quaternary/10"
                    : "border-gray-200 hover:border-quaternary/50"
                }`}
            >
              <span className="text-lg font-medium text-quinary">
                {tenant.name}
              </span>
              {/* {tenant.logo && (
                <img
                  src={tenant.logo}
                  alt={`${tenant.name} logo`}
                  className="w-12 h-12 object-contain"
                />
              )} */}
            </button>
          ))}
        </div>

        <div className="mt-8 text-center text-sm text-quinary/70">
          Select the organization you want to access the system for
        </div>
      </div>
    </div>
  );
};
